import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  FilterState,
  FilterConfig,
  FilterContextValue,
} from './types';

// ============================================================================
// Context
// ============================================================================

const FilterContext = createContext<FilterContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access filter context.
 * Must be used within a FilterProvider.
 *
 * @example
 * ```tsx
 * function FilterDisplay() {
 *   const { filters, setFilter, hasActiveFilters } = useFilterContext<EmployeeFilters>();
 *   return <div>{hasActiveFilters && 'Filters active'}</div>;
 * }
 * ```
 */
export function useFilterContext<
  TFilters extends FilterState = FilterState,
>(): FilterContextValue<TFilters> {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error(
      'useFilterContext must be used within a FilterProvider. ' +
        'Make sure to wrap your component with <FilterProvider>.'
    );
  }

  return context as unknown as FilterContextValue<TFilters>;
}

// ============================================================================
// Provider
// ============================================================================

interface FilterProviderProps<TFilters extends FilterState> {
  /** Filter configuration defining fields and defaults */
  config: FilterConfig<TFilters>;
  /** Controlled value (optional - uses internal state if not provided) */
  value?: TFilters;
  /** Change handler for controlled mode */
  onChange?: (filters: TFilters) => void;
  /** Children components */
  children: ReactNode;
}

/**
 * Provider component for filter state management.
 * Supports both controlled and uncontrolled modes.
 *
 * @example Controlled mode
 * ```tsx
 * const [filters, setFilters] = useState(config.defaultValues);
 * <FilterProvider config={config} value={filters} onChange={setFilters}>
 *   <FilterSheet />
 * </FilterProvider>
 * ```
 *
 * @example Uncontrolled mode
 * ```tsx
 * <FilterProvider config={config}>
 *   <FilterSheet />
 * </FilterProvider>
 * ```
 */
export function FilterProvider<TFilters extends FilterState>({
  config,
  value,
  onChange,
  children,
}: FilterProviderProps<TFilters>) {
  // Internal state for uncontrolled mode
  const [internalFilters, setInternalFilters] = useState<TFilters>(
    () => value ?? config.defaultValues
  );

  // Use controlled value if provided, otherwise use internal state
  const filters = value ?? internalFilters;
  const isControlled = value !== undefined;

  // Set all filters at once
  const setFilters = useCallback(
    (newFilters: TFilters) => {
      if (isControlled && onChange) {
        onChange(newFilters);
      } else {
        setInternalFilters(newFilters);
      }
    },
    [isControlled, onChange]
  );

  // Set a single filter value
  const setFilter = useCallback(
    <K extends keyof TFilters>(key: K, filterValue: TFilters[K]) => {
      const updated = { ...filters, [key]: filterValue } as TFilters;

      // Call onChange callback from config if provided
      config.onChange?.(updated, key as string);

      setFilters(updated);
    },
    [filters, setFilters, config]
  );

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    config.onClear?.();
    setFilters(config.defaultValues);
  }, [setFilters, config]);

  // Calculate active filter state
  const { hasActiveFilters, activeFilterCount } = useMemo(() => {
    let count = 0;

    for (const field of config.fields) {
      const currentValue = filters[field.key as keyof TFilters];
      const defaultValue = config.defaultValues[field.key as keyof TFilters];

      // Check if value differs from default
      if (Array.isArray(currentValue)) {
        // For arrays, check if not empty
        if (currentValue.length > 0) {
          count++;
        }
      } else if (currentValue !== defaultValue) {
        // Special case: 'all' is considered as no filter
        if (currentValue !== 'all') {
          count++;
        }
      }
    }

    return {
      hasActiveFilters: count > 0,
      activeFilterCount: count,
    };
  }, [filters, config.fields, config.defaultValues]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<FilterContextValue<TFilters>>(
    () => ({
      filters,
      setFilters,
      setFilter,
      resetFilters,
      hasActiveFilters,
      activeFilterCount,
      config,
    }),
    [
      filters,
      setFilters,
      setFilter,
      resetFilters,
      hasActiveFilters,
      activeFilterCount,
      config,
    ]
  );

  return (
    <FilterContext.Provider value={contextValue as unknown as FilterContextValue}>
      {children}
    </FilterContext.Provider>
  );
}
